/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, { useCallback, useMemo } from "react";

import { usePlayerComponents } from "../../../../../shared/context/PlayerComponentsContext";
import { usePlayerTranslation } from "../../../../../shared/context/PlayerTranslationContext";
import PlayIcon from "../../../../../shared/icons/player/Play.svg";
import RepeatIcon from "../../../../../shared/icons/player/Repeat.svg";
import { formatDurationMs } from "../../../../../shared/utils/time";
import { usePlayerController } from "../../../../BasePlayer";
import {
  usePlayerContinueWatching,
  usePlayerCurrentMedia,
} from "../../../../Controller";
import { DialogBox } from "../../../../Interface/Base";

import "./ContinueWatchingDialog.scss";

export const ContinueWatchingDialog = () => {
  const { Button, Slider } = usePlayerComponents();
  const { t } = usePlayerTranslation();

  const { media } = usePlayerCurrentMedia();
  const { player } = usePlayerController();
  const { startPosition } = usePlayerContinueWatching();

  const onContinueWatching = useCallback(() => {
    player.resume(startPosition);
  }, [player, startPosition]);

  const onStartOver = useCallback(() => {
    player.play();
  }, [player]);

  const content = useMemo(() => {
    const duration = media.Duration ? media.Duration / 1000 : 0;
    const sliderPosition = duration ? startPosition / duration : 0;

    return (
      <>
        <p>{t("PLAYER_CONTINUE_WATCHING_HEADER")}</p>
        {duration > 0 && (
          <div className="ContinueWatchingDialog__Progress">
            {sliderPosition > 0 && (
              <div className="ContinueWatchingDialog__Progress__Slider">
                <Slider
                  value={sliderPosition}
                  aria-label={t("PLAYER_CONTINUE_WATCHING_HEADER")}
                  readOnly
                />
              </div>
            )}
            <span>{formatDurationMs(startPosition * 1000, "HH:mm:ss")}</span>
          </div>
        )}
      </>
    );
  }, [Slider, startPosition, media.Duration, t]);

  const actions = useMemo(() => {
    return (
      <>
        <Button variant="secondary" onClick={onStartOver}>
          <RepeatIcon aria-hidden="true" focusable="false" />
          {t("PLAYER_BUTTON_CONTINUE_WATCHING_FROM_START")}
        </Button>
        <Button variant="primary" onClick={onContinueWatching}>
          <PlayIcon aria-hidden="true" focusable="false" />
          {t("PLAYER_BUTTON_CONTINUE_WATCHING_RESUME")}
        </Button>
      </>
    );
  }, [Button, onContinueWatching, onStartOver, t]);

  return <DialogBox content={content} actions={actions} />;
};
